import type * as mdast from 'mdast';

import {
    BLOCK_CODE,
    BLOCK_PARAGRAPH,
    BLOCK_TABLE,
    type BlockTableData,
    table as docCoreTable,
    type TableGridView,
} from '@gitbook/doc-core';
import type { Block, Fragment, Inline } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

import { formatHTML } from '../html';
import type { FormattingOptions } from '../options';
import type { GastToMdastTransformMap } from './types';

/**
 * A list of nodes that cannot be rendered as markdown, because they would
 * break the table structure. If any of these is encountered, we'll render
 * the table as HTML instead.
 */
const MARKDOWN_INCOMPATIBLE_NODES = [
    // Code blocks cannot be rendered as markdown because they may contain newlines
    BLOCK_CODE,
];

/**
 * Convert a table block into Markdown nodes.
 */
export const transformTableBlocks: GastToMdastTransformMap<Block> = {
    [BLOCK_TABLE]: async (node, eat, options) => {
        const data = node.data.toObject<BlockTableData>();

        const columnIds = Object.keys(data.definition);

        // Complex tables cannot be represented as markdown
        if (!isTableMarkdownCompatible(data, node)) {
            return [
                {
                    type: 'html',
                    value: await formatHTML(node, options),
                },
            ];
        }

        const view = data.view as TableGridView;

        // All columns are displayed, but we start with the visible ones
        const firstColumnIds = view.columns;
        const otherColumnIds = Object.keys(data.definition).filter(
            (key) => !firstColumnIds.includes(key)
        );
        const orderedColumns = [...firstColumnIds, ...otherColumnIds];

        const rows: mdast.TableRow[] = [];

        // Render table header as a first row
        const hasColumnWithTitle = columnIds.some(
            (columnId) => typeof data.definition[columnId].title !== 'undefined'
        );
        if (!view.hideHeader && hasColumnWithTitle) {
            rows.push({
                type: 'tableRow',
                children: orderedColumns.map((columnId) => {
                    return {
                        type: 'tableCell',
                        children: [{ type: 'text', value: data.definition[columnId].title }],
                    };
                }),
            });
        }

        // Render records ordered
        const recordKeys = docCoreTable.defaultRecordSortedKeys(data);
        for (const recordKey of recordKeys) {
            const record = data.records[recordKey];

            rows.push({
                type: 'tableRow',
                children: await Promise.all(
                    orderedColumns.map(async (columnId): Promise<mdast.TableCell> => {
                        const value = record.values[columnId];

                        return {
                            type: 'tableCell',
                            children: await fragmentToMdast(
                                // @ts-expect-error
                                node.getFragmentByKey(value),
                                eat,
                                options
                            ),
                        };
                    })
                ),
            });
        }

        const align = columnIds.map((columnId) => {
            const definition = data.definition[columnId];
            // only add the alignment if it's not the default
            return definition.type === 'text' && definition.textAlignment !== 'left'
                ? definition.textAlignment
                : null;
        });

        return [
            {
                type: 'table',
                align,
                children: rows,
            },
        ];
    },
};

/**
 * Transform a Fragment into a list of UnifiedNodes. If the child
 * nodes can be represented as Markdown within a table, they will
 * be translated to Markdown. Otherwise, we'll transform them into
 * HTML.
 */
async function fragmentToMdast(
    fragment: Fragment,
    eat: (children: List<Text | Inline>) => Promise<mdast.PhrasingContent[]>,
    options: FormattingOptions
): Promise<mdast.PhrasingContent[]> {
    // We need HTML for any case that can generate a newline
    const needsHTML =
        fragment.nodes.size > 1 ||
        fragment.nodes.first(null)?.type !== BLOCK_PARAGRAPH ||
        fragment.nodes.first(null)?.text.includes('\n');

    // Some blocks (such as lists) cannot be nested in Table cells.
    // If we have a nested block like this, serialize it to HTML instead
    // of Markdown.
    if (needsHTML) {
        const hast = await formatHTML(fragment, options);
        return [{ type: 'html', value: hast }];
    }
    return eat(fragment.nodes.first()?.nodes as List<Text | Inline>);
}

/**
 * Test if a table can be output as Markdown: it must basically be a only-text table.
 */
function isTableMarkdownCompatible(table: BlockTableData, node: Block): boolean {
    const structureCompatible =
        table.view.type === 'grid' &&
        Object.keys(table.definition).every((columnId) => {
            const definition = table.definition[columnId];

            return (
                definition.type === 'text' &&
                table.view.columns.includes(columnId) &&
                !definition.verticalAlignment
            );
        }) &&
        typeof table.fullWidth === 'undefined' &&
        Object.keys(table.view.columnWidths ?? {}).length === 0 &&
        // @ts-expect-error
        !table.view.verticalAlignment;

    if (!structureCompatible) {
        return false;
    }

    // Check if any cell contains incompatible content that requires HTML
    const recordKeys = Object.keys(table.records);

    for (const columnId of Object.keys(table.definition)) {
        if (table.definition[columnId].type !== 'text') {
            continue;
        }

        for (const recordKey of recordKeys) {
            const record = table.records[recordKey];
            const value = record.values[columnId];

            if (value === null) {
                continue;
            }

            const fragment = node.getFragmentByKey(value as string);

            if (fragment) {
                // Check if the fragment has markdown-incompatible nodes
                const hasIncompatibleNodes = fragment.nodes.some((node) =>
                    MARKDOWN_INCOMPATIBLE_NODES.includes(node.type)
                );

                if (hasIncompatibleNodes) {
                    return false;
                }
            }
        }
    }

    return true;
}
